/**
 * features/setup/TeachersSetup.tsx
 * ------------------------------------------------------------------
 * Round 5 redesign — list view only.
 *
 * The full teacher edit surface lives in TeacherProfile (deep-linked
 * via #/teacherProfile/<id>). This screen is now ONLY:
 *   - Search/filter the teacher list
 *   - "New teacher" — opens a minimal modal (title/initials/lastname/email)
 *     to create the record. The user is then taken to TeacherProfile
 *     to fill in teaching assignments + everything else.
 *   - Pencil icon on each row → opens TeacherProfile
 *   - Trash icon to delete
 *
 * No more inline modal for full editing — the profile is the source
 * of truth.
 *
 * HR RECORD (Oct 2026). Creating a teacher here also creates their HR
 * staff record (lib/teacherHr), so they reach HR, payroll and the clocking
 * upload without anyone re-entering them — teachers added after the June
 * migration had been arriving without one. A teacher still without an HR
 * record shows "Not in HR" with an Add to HR button. For the academic
 * administrator (clocking uploader) and HR; firestore.rules allows the
 * former to create only that record, never to edit HR.
 * ------------------------------------------------------------------
 */
import { useEffect, useState, useMemo } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { hasHrRecord, type StaffLike } from '@/lib/teacherHr';
import { createTeacherHrRecord } from '@/features/setup/teacherHrWrite';
// Batch A: undefined-safe write + save modal
import { addDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  BookOpen,
  GraduationCap,
  ExternalLink,
  UserPlus,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeText, sanitizeEmail } from '@/lib/sanitize';
import { Button, Card, PageHeader, Spinner } from '@/components/ui/primitives';
import { FormModal } from '@/components/ui/glass';
import type { Teacher } from '@/types';

const TITLES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Mma', 'Rre'];

interface NewTeacherForm {
  title: string;
  initials: string;
  lastName: string;
  email: string;
}

const EMPTY_NEW: NewTeacherForm = {
  title: 'Mr',
  initials: '',
  lastName: '',
  email: '',
};

export default function TeachersSetup() {
  const { teachers, classes, loading } = useData();
  const { userData, permissions } = useAuth();
  // Who may give a teacher an HR record from here: the academic administrator
  // (clocking uploader) and HR. The HR list is read only for them.
  const canMakeHr = permissions.canImportClocking || permissions.canAccessHR;
  const [hrStaff, setHrStaff] = useState<StaffLike[] | null>(null);
  const [hrBusy, setHrBusy] = useState<string | null>(null);
  useEffect(() => {
    if (!canMakeHr) return undefined;
    return onSnapshot(
      collection(db, 'hrStaff'),
      (snap) => setHrStaff(snap.docs.map((d) => ({ ...(d.data() as Omit<StaffLike, 'id'>), id: d.id }))),
      () => setHrStaff(null),
    );
  }, [canMakeHr]);

  async function addToHr(t: Teacher, quiet = false): Promise<string | null> {
    if (!hrStaff) return null;
    setHrBusy(t.id);
    try {
      const no = await createTeacherHrRecord(t, hrStaff, { uid: userData?.uid, email: userData?.email });
      if (!quiet) setMsg({ ok: true, text: `${t.displayName} added to HR as ${no}. HR completes the start date, residency and pay.` });
      return no;
    } catch (e) {
      setMsg({ ok: false, text: `Could not add ${t.displayName} to HR: ${(e as Error).message}` });
      return null;
    } finally {
      setHrBusy(null);
    }
  }
  const [showAddModal, setShowAddModal] = useState(false);
  const [newForm, setNewForm] = useState<NewTeacherForm>(EMPTY_NEW);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Teacher | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return [...teachers]
      .filter((t) => !q || t.displayName?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q))
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  }, [teachers, search]);

  function classNameById(id: string): string {
    return classes.find((c) => c.id === id)?.name ?? id;
  }

  function buildNames(f: NewTeacherForm) {
    const initials = f.initials.toUpperCase().replace(/[^A-Z]/g, '');
    const fullName = `${initials} ${f.lastName}`.trim();
    const displayName = `${f.title} ${f.lastName}`.trim();
    return { initials, fullName, displayName };
  }

  function openAdd() {
    setNewForm(EMPTY_NEW);
    setMsg(null);
    setShowAddModal(true);
  }

  async function saveNewTeacher() {
    const lastName = sanitizeText(newForm.lastName, 40);
    if (!lastName) {
      setMsg({ ok: false, text: 'Last name is required.' });
      return;
    }
    setBusy(true);
    setSaveModal({ show: true, status: 'saving', message: 'Creating teacher…' });
    try {
      const { initials, fullName, displayName } = buildNames(newForm);
      const now = new Date().toISOString();
      const newTeacher = {
        title: newForm.title,
        initials,
        lastName,
        fullName,
        displayName,
        email: sanitizeEmail(newForm.email),
        classAssignments: {},
        teachingAssignments: {},
        isClassTeacher: false,
        classTeacherOf: '',
        isAssistantClassTeacher: false,
        assistantClassTeacherOf: [],
        createdAt: now,
        createdBy: userData?.uid,
        updatedAt: now,
        updatedBy: userData?.uid,
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.teachers), newTeacher);
      // Their HR record, so HR, payroll and the clocking upload have them too.
      if (canMakeHr && hrStaff) {
        await addToHr({ ...(newTeacher as unknown as Teacher), id: docRef.id }, true);
      }
      setShowAddModal(false);
      // Close the saving modal — the user is about to navigate away
      // to TeacherProfile, so a success modal would only appear briefly.
      setSaveModal({ show: false, status: 'saving', message: '' });
      // Jump straight to their profile so the admin can fill in the rest
      window.location.hash = `#/teacherProfile/${docRef.id}`;
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function remove(t: Teacher) {
    setBusy(true);
    try {
      await deleteDoc(doc(db, COLLECTIONS.teachers, t.id));
      setMsg({ ok: true, text: `Removed ${t.displayName}.` });
      setConfirmDelete(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label="Loading teachers…" />;

  return (
    <div>
      <PageHeader
        title="Teachers"
        subtitle={`${teachers.length} teacher${teachers.length === 1 ? '' : 's'}`}
      />

      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <Button onClick={openAdd}>
            <Plus size={14} /> New teacher
          </Button>
        </div>

        {msg && (
          <div className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            {msg.text}
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-center text-gray-400 py-10">
            {search ? 'No teachers match your search.' : 'No teachers yet. Add one to get started.'}
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((t) => {
              const subjectCount = Object.keys(t.classAssignments ?? {}).filter(
                (sid) => (t.classAssignments![sid] ?? []).length > 0,
              ).length;
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                    {(t.initials || t.lastName?.[0] || '?').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-800 dark:text-white truncate">
                      {t.displayName}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                      {t.email && <span className="truncate">{t.email}</span>}
                      {t.department && (
                        <>
                          <span>·</span>
                          <span>{t.department}</span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[10px]">
                      {subjectCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          <BookOpen size={11} />
                          {subjectCount} subject{subjectCount === 1 ? '' : 's'}
                        </span>
                      )}
                      {t.classTeacherOf && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">
                          <GraduationCap size={11} />
                          Class teacher: {classNameById(t.classTeacherOf)}
                        </span>
                      )}
                      {t.assistantClassTeacherOf && t.assistantClassTeacherOf.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          <GraduationCap size={11} />
                          Assistant: {t.assistantClassTeacherOf.map(classNameById).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canMakeHr && hrStaff && !hasHrRecord(t.id, hrStaff) && (
                      <button
                        onClick={() => addToHr(t)}
                        disabled={hrBusy === t.id}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-200 text-xs font-medium disabled:opacity-50"
                        title="Not in HR yet — create their HR staff record, linked to this teacher"
                      >
                        <UserPlus size={14} />
                        <span className="hidden sm:inline">{hrBusy === t.id ? 'Adding…' : 'Add to HR'}</span>
                      </button>
                    )}
                    <a
                      href={`#/teacherProfile/${t.id}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-primary/10 text-primary text-xs font-medium"
                      title="Open full profile"
                    >
                      <Pencil size={14} />
                      <span className="hidden sm:inline">Edit</span>
                    </a>
                    <button
                      onClick={() => setConfirmDelete(t)}
                      className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500"
                      title="Delete teacher"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add modal — MINIMAL: title/initials/lastname/email */}
      <FormModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        icon={GraduationCap}
        title="New teacher"
        maxWidth="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowAddModal(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={saveNewTeacher} loading={busy}>
              Create & open profile
            </Button>
          </>
        }
      >
            <p className="text-xs text-gray-500 mb-5">
              Create the teacher record. You'll fill in teaching assignments and other details on the next screen.
            </p>

            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title
                  </label>
                  <select
                    value={newForm.title}
                    onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                    className="w-full px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                  >
                    {TITLES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Initials *
                  </label>
                  <input
                    type="text"
                    value={newForm.initials}
                    onChange={(e) => setNewForm({ ...newForm, initials: e.target.value })}
                    placeholder="JK"
                    className="w-full px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last name *
                  </label>
                  <input
                    type="text"
                    value={newForm.lastName}
                    onChange={(e) => setNewForm({ ...newForm, lastName: e.target.value })}
                    placeholder="Surname"
                    className="w-full px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newForm.email}
                  onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
                  placeholder="teacher@lkc.ac.bw"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
                />
              </div>
              <p className="text-xs text-gray-500 flex items-start gap-1.5 pt-2">
                <ExternalLink size={12} className="mt-0.5 shrink-0" />
                After creating, you'll be taken to the teacher's profile to set teaching assignments and other details.
              </p>
            </div>

            {msg && !msg.ok && (
              <div className="mt-3 text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">
                {msg.text}
              </div>
            )}
      </FormModal>


      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card glass className="w-full max-w-sm p-6 animate-fade-in">
            <h2 className="text-lg font-bold">Remove teacher?</h2>
            <p className="text-sm text-gray-500 mt-2">
              Remove <strong>{confirmDelete.displayName}</strong>? This won't delete their past marks or comments, but they will no longer appear in pickers.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={() => remove(confirmDelete)} loading={busy}>
                Remove
              </Button>
            </div>
          </Card>
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
